import type { BalanceRow, CashPositionMonth } from '../api'
import { monthLabel } from '../format'
import { plotValue } from './scale'

// Month-end actuals view of the Cash position series (GTM-54): each month is
// an actual (a real month-end bank balance), missing (the month ended with no
// balance known: a gap, never interpolated) or projected (not ended yet).

export type CashPoint = {
  month: string
  kind: 'actual' | 'missing' | 'projected'
  /** The booked line. Actual: the balance. Missing: null. */
  booked: number | null
  /** The with-pipeline line. Actual: the same balance. Missing: null. */
  withPipeline: number | null
  actualSource: 'audited' | 'freee_month_end' | null
}

export function toCashPoints(months: CashPositionMonth[]): CashPoint[] {
  return months.map((m): CashPoint => {
    if (m.pointKind === 'actual') {
      const balance = plotValue(m.actualBalance)
      // An actual with no readable balance is a gap, not a zero.
      if (balance !== null) {
        return {
          month: m.month,
          kind: 'actual',
          booked: balance,
          withPipeline: balance,
          actualSource: m.actualSource === 'audited' ? 'audited' : 'freee_month_end',
        }
      }
    }
    if (m.pointKind === 'projected') {
      return {
        month: m.month,
        kind: 'projected',
        booked: plotValue(m.projectedBalanceExPipeline),
        withPipeline: plotValue(m.projectedBalance),
        actualSource: null,
      }
    }
    return { month: m.month, kind: 'missing', booked: null, withPipeline: null, actualSource: null }
  })
}

/**
 * freee's live balance for the month in progress: the freee reconciliation row
 * of the first projected month (the current JST month). Null without one.
 */
export function liveBalanceFor(
  months: CashPositionMonth[],
  recon: BalanceRow[],
): { month: string; balance: string } | null {
  const current = months.find((m) => m.pointKind === 'projected')
  if (!current) return null
  const row = recon.find((r) => r.month === current.month && r.source === 'freee')
  return row ? { month: row.month, balance: row.auditedBalance } : null
}

export const LIVE_NOTE = 'Live bank balance today (not month-end)'

/** The one-line explanation of a point, as the tooltip words it. */
export function pointNote(point: CashPoint, projectionAnchorMonth: string | null): string {
  if (point.kind === 'actual') {
    return point.actualSource === 'audited'
      ? 'Actual · audited month-end balance'
      : 'Actual · month-end bank balance (freee)'
  }
  if (point.kind === 'missing') return 'No bank balance recorded for this month'
  return projectionAnchorMonth
    ? `Projected from the ${monthLabel(projectionAnchorMonth)} actual`
    : 'Projected'
}
